import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ASSETS } from "../lib/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 },
  }),
};

const Masked = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className={`block ${className || ""}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mascotY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const mascotRot = useTransform(scrollYProgress, [0, 1], [0, 18]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16">
      {/* moody beans backdrop */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={ASSETS.beansMoody} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/40 to-void" />
      </motion.div>

      {/* giant mascot, parallax — framed in a cream coin so the mark pops on dark */}
      <motion.div
        style={{ y: mascotY, rotate: mascotRot }}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className="pointer-events-none select-none relative mx-auto mb-8 w-[52%] max-w-[230px] md:max-w-[520px] md:mx-0 md:mb-0 md:absolute md:right-[3%] md:top-[12%] md:w-[38%] lg:w-[33%] z-20"
      >
        <div className="relative aspect-square rounded-full overflow-hidden bg-cream ring-[6px] ring-ember shadow-[0_25px_60px_rgba(0,0,0,0.6)]">
          <img src={ASSETS.mascot} alt="Gremlin mascot" className="w-full h-full object-cover scale-110" />
        </div>
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-ember mb-6"
        >
          Nashville · Air Roasted · Ships across North America
        </motion.p>

        <h1 className="font-display font-black tracking-tight uppercase leading-[0.88] text-cream text-[2.9rem] sm:text-7xl lg:text-[8.5rem]">
          <Masked i={0}>Smooth</Masked>
          <Masked i={1}>Coffee<span className="ml-[0.12em]">.</span></Masked>
          <Masked i={2} className="text-ember">Gremlin</Masked>
          <Masked i={3}>
            <span className="font-serif italic font-normal lowercase text-cream">energy.</span>
          </Masked>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-9 max-w-xl"
        >
          <p className="font-mono text-sm text-cream/70 leading-relaxed max-w-sm">
            Air-roasted beans smooth enough to drink black. No bitterness, just flavor and a little chaos.
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-5 md:left-10 hidden md:flex items-center gap-3 text-cream/55 font-mono text-[10px] uppercase tracking-widest"
      >
        <ArrowDown size={14} className="animate-bounce" /> Scroll · No Bitterness Ahead
      </motion.div>
    </section>
  );
};
